import { parseDecimal } from '../../utils';

type ShamirRXMaterial = 'Organiczne' | 'Mineralne';

const INDEX_RULES: { pattern: RegExp; value: string }[] = [
    { pattern: /\b1\.523\b/, value: '1,523' },
    { pattern: /\b1\.74\b/, value: '1,74' },
    { pattern: /\b1\.67\b/, value: '1,67' },
    { pattern: /\b1\.59\b/, value: '1,59' },
    { pattern: /\b1\.53\b/, value: '1,53' },
    { pattern: /\b1\.6\b/, value: '1,6' },
    { pattern: /\b1\.5\b/, value: '1,5' },
    { pattern: /\b1\.8\b/, value: '1,8' },
    { pattern: /\b1\.9\b/, value: '1,9' },
    { pattern: /\b1\.7\b/, value: '1,7' },
];

const SPH_MIN_BY_INDEX: Record<string, string> = {
    '1,5': '-14',
    '1,53': '-14',
    '1,59': '-15',
    '1,6': '-15',
    '1,67': '-17',
    '1,74': '-20',
    '1,8': '-19',
    '1,9': '-20',
    '1,7': '-15,5',
    '1,523': '-10',
};

const SPH_MAX_BY_INDEX: Record<string, string> = {
    '1,5': '18',
    '1,53': '15',
    '1,523': '15',
    '1,59': '16',
    '1,6': '16',
    '1,67': '24',
    '1,74': '24,5',
    '1,8': '9',
    '1,9': '9',
    '1,7': '8',
};

const CYL_MAX_BY_INDEX: Record<string, string> = {
    '1,74': '9',
    '1,8': '9',
    '1,9': '9',
    '1,67': '8',
    '1,7': '8',
    '1,53': '7',
    '1,59': '7',
    '1,6': '7',
    '1,5': '6',
    '1,523': '6',
};

export const getShamirRXIndex = (name: string) => {
    const matchingRule = INDEX_RULES.find(({ pattern }) => pattern.test(name));

    return matchingRule?.value ?? 0;
};

export const getShamirRXMaterial = (materialCode: string): ShamirRXMaterial =>
    materialCode.toUpperCase() === 'M' ? 'Mineralne' : 'Organiczne';

export const getShamirRXIsPhotochromic = (name: string) =>
    /Xtractive/i.test(name)
    || /Xtra\./i.test(name)
    || /\bTGNS\b/i.test(name)
    || /Photogrey/i.test(name)
    || /Photobrown/i.test(name);

export const getShamirRXPhotochromicColor = (name: string) => {
    if (/Brown/i.test(name) || /Photobrown/i.test(name) || /\bBR\b/i.test(name)) {
        return 'brązowy';
    }

    if (/\bGR\b/i.test(name) || /Grey/i.test(name) || /Photogrey/i.test(name)) {
        return 'szary';
    }

    if (/\bGN\b/i.test(name)) {
        return 'szarozielony';
    }

    if (/Sapphire/i.test(name)) {
        return 'szafirowy';
    }

    if (/Amber/i.test(name)) {
        return 'bursztynowy';
    }

    if (/Emerald/i.test(name)) {
        return 'szmaragdowy';
    }

    if (/Amethyst/i.test(name)) {
        return 'ametystowy';
    }

    if (/Ruby/i.test(name)) {
        return 'rubinowy';
    }

    return 0;
};

export const getShamirRXIsPolarising = (name: string) => /\bPolar\b/i.test(name);

export const getShamirRXPolarisingColor = (name: string) => {
    if (/Polar Brown/i.test(name)) {
        return 'brązowy';
    }

    if (/Polar Grey/i.test(name)) {
        return 'szary';
    }

    if (/Polar Green/i.test(name)) {
        return 'zielony';
    }

    return 0;
};

export const getShamirRXLensType = (name: string) => {
    if (/Relax/i.test(name)) {
        return 'Relaksacyjne';
    }

    if (/Computer/i.test(name) || /Workspace/i.test(name) || /Office/i.test(name)) {
        return 'do bliży i odległości pośrednich';
    }

    if (/Duo/i.test(name) || /Bifocal/i.test(name)) {
        return 'Dwuogniskowe';
    }

    if (/\bSV\b/i.test(name)) {
        return 'Jednoogniskowe';
    }

    return 'Progresywne';
};

export const getShamirRXIsColored = (name: string) =>
    /Color/i.test(name) || /\bPolar\b/i.test(name) || /PhotoGlamour/i.test(name);

export const getShamirRXFrameType = (name: string, material: ShamirRXMaterial) => {
    if (material === 'Mineralne') {
        return 'pełna';
    }

    const index = getShamirRXIndex(name);

    if (index === '1,5' || index === '1,74') {
        return 'pełna, żyłka';
    }

    if (index === '1,6' || index === '1,53' || index === '1,59' || index === '1,67') {
        return 'pełna, żyłka, patent';
    }

    return 0;
};

export const getShamirRXOpticalRange = (name: string) => {
    const index = getShamirRXIndex(name);

    return {
        sphMin: SPH_MIN_BY_INDEX[index] ?? 0,
        sphMax: SPH_MAX_BY_INDEX[index] ?? 0,
        cylMax: CYL_MAX_BY_INDEX[index] ?? 0,
    };
};

export const sumShamirRXDecimals = (left: string | number, right: string | number) => {
    const leftValue = typeof left === 'number' ? left : parseDecimal(left);
    const rightValue = typeof right === 'number' ? right : parseDecimal(right);
    const sum = leftValue + rightValue;
    const formatted = String(sum).replace('.', ',');

    if (sum > 0) {
        return `+${formatted}`;
    }

    if (sum < 0) {
        return formatted;
    }

    return formatted;
};
